import { Component, DOCUMENT, inject, OnDestroy, OnInit } from '@angular/core';
import { LoaderService } from '@ddv/behaviors';
import { VersionsService } from '@ddv/dashboards';
import { DataGridUtilities } from '@ddv/data-grid';
import { UserService } from '@ddv/entitlements';
import { Subscription } from 'rxjs';

import { ApplicationService } from './application.service';
import { ClientCodeService } from './client-code.service';
import { durationBeforeMandatoryRefreshMinutes, pollIntervalMs } from './version-polling.constants';
import { environment } from '../environments/environment';
import { WrapperAppBindingService } from './wrapper/wrapper-app-binding.service';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false,
})
export class AppComponent implements OnInit, OnDestroy {
    private readonly applicationService = inject(ApplicationService);
    private readonly loaderService = inject(LoaderService);
    private readonly wrapperAppBinding = inject(WrapperAppBindingService);
    private readonly clientCodeService = inject(ClientCodeService);
    private readonly document = inject(DOCUMENT);
    private readonly versionsService = inject(VersionsService);
    private readonly userService = inject(UserService);

    protected isLoading: boolean | undefined;
    protected clientCode: string | undefined;
    protected appDisplayName: string | undefined;
    protected autoDismissErrors = false;
    protected currentVersion = '';
    protected latestVersion = '';
    protected versions: Record<string, string> = {
        'DDV UI version': '',
    };

    protected durationBeforeMandatoryRefreshMinutes = durationBeforeMandatoryRefreshMinutes;
    private readonly subscriptions: Subscription[] = [];

    constructor() {
        this.wrapperAppBinding.startup();

        this.loaderService.isLoading()
            .subscribe({
                next: (loading) => this.isLoading = loading,
            });

        const versionsSubscription = this.versionsService.getVersions()
            .subscribe({
                next: (versions) => {
                    this.versions = versions;

                    const uiVersion = versions['DDV UI version'];
                    if (uiVersion) {
                        this.currentVersion = uiVersion;
                        this.latestVersion = uiVersion;
                    }
                },
            });

        this.subscriptions.push(versionsSubscription);
    }

    ngOnInit(): void {
        this.applicationService.registerInterceptors();
        DataGridUtilities.setThemeClass('ag-theme-blue');
        DataGridUtilities.registerLicenseKey(environment.agGridLicenseKey);

        this.subscriptions.push(
            this.clientCodeService.clientCode$.subscribe({
                next: (clientCode) => this.clientCode = clientCode,
            }),
        );

        this.subscriptions.push(
            this.userService.userPreferences$.subscribe({
                next: (prefs) => this.autoDismissErrors = prefs.autoDismissErrors,
            }),
        );

        this.appDisplayName = appNameFromURL(this.document.location.hostname, this.document.location.pathname);

        this.pollUiVersion();
    }

    private pollUiVersion(): void {
        const pollingSubscription = this.versionsService.pollUiVersion(pollIntervalMs)
            .subscribe({
                next: (newVersion) => {
                    if (newVersion && newVersion !== this.latestVersion) {
                        this.latestVersion = newVersion;
                    }
                },
            });

        this.subscriptions.push(pollingSubscription);
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach((sub) => sub.unsubscribe());
    }
}

export function appNameFromURL(hostname: string, path: string): string {
    const hostnameParts = hostname.toLowerCase().split('.');
    if (hostnameParts[0] === 'ddv') {
        if (hostnameParts[1] === 'poc-rnd') {
            return 'DDV (RND)';
        }

        return 'DDV';
    }

    if (path.includes('-edge')) {
        return 'DDV (Edge)';
    }

    if (path.includes('-aws-rnd')) {
        return 'DDV (RND)';
    }

    return 'DDV';
}
